import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  Spinner,
  useToast,
  useDisclosure,
  Flex,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import {
  AddIcon,
  ViewIcon,
  TimeIcon,
  CheckIcon,
  WarningIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import {
  getProspectLists,
  getPlacesNeedingReturn,
  deleteProspectList,
} from "../services/api";
import NovaListaModal from "../components/NovaListaModal";
import ProspectListCard from "../components/ProspectListCard";
import ReturnNotifications from "../components/ReturnNotifications";

const Prospeccao = () => {
  const [prospectLists, setProspectLists] = useState([]);
  const [placesNeedingReturn, setPlacesNeedingReturn] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLists: 0,
    completedLists: 0,
    totalPlaces: 0,
    contactedPlaces: 0,
    convertedPlaces: 0,
    pendingReturns: 0,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const [listsResult, returnsResult] = await Promise.all([
        getProspectLists(),
        getPlacesNeedingReturn(),
      ]);

      if (listsResult) {
        setProspectLists(listsResult.data || []);

        // Calcular estatísticas
        const totalLists = listsResult.data.length;
        const completedLists = listsResult.data.filter(list => list.is_completed).length;
        const totalPlaces = listsResult.data.reduce((sum, list) => sum + list.total_places, 0);
        const contactedPlaces = listsResult.data.reduce((sum, list) => sum + list.contacted_places, 0);
        const convertedPlaces = listsResult.data.reduce((sum, list) => sum + list.converted_places, 0);

        setStats({
          totalLists,
          completedLists,
          totalPlaces,
          contactedPlaces,
          convertedPlaces,
          pendingReturns: returnsResult.length,
        });
      }

      if (returnsResult) {
        setPlacesNeedingReturn(returnsResult);
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar dados de prospecção",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewListCreated = (newList) => {
    setProspectLists(prev => [newList, ...prev]);
    setStats(prev => ({
      ...prev,
      totalLists: prev.totalLists + 1,
      totalPlaces: prev.totalPlaces + newList.total_places,
    }));
    onClose();
  };

  const handleDeleteList = async (listId) => {
    if (window.confirm("Tem certeza que deseja excluir esta lista de prospecção?")) {
      const result = await deleteProspectList(listId);
      if (result) {
        toast({
          title: "Lista excluída",
          description: "Lista de prospecção excluída com sucesso",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        loadData();
      } else {
        toast({
          title: "Erro",
          description: "Erro ao excluir lista",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="center">
          <Spinner size="xl" />
          <Text>Carregando dados de prospecção...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="gray.700">
              Prospecção
            </Heading>
            <Text color="gray.600" fontSize="md">
              Gerencie suas listas de prospecção e acompanhe o progresso
            </Text>
          </VStack>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={onOpen}
            size="md"
          >
            Nova Lista
          </Button>
        </Flex>

        {/* Estatísticas Gerais */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }} gap={4}>
          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Listas Totais</StatLabel>
                  <StatNumber color="blue.500">{stats.totalLists}</StatNumber>
                  <StatHelpText>
                    {stats.completedLists} completas
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Total de Lugares</StatLabel>
                  <StatNumber color="purple.500">{stats.totalPlaces}</StatNumber>
                  <StatHelpText>
                    para prospecção
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Contactados</StatLabel>
                  <StatNumber color="green.500">{stats.contactedPlaces}</StatNumber>
                  <StatHelpText>
                    {stats.totalPlaces > 0 ? Math.round((stats.contactedPlaces / stats.totalPlaces) * 100) : 0}% do total
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Convertidos</StatLabel>
                  <StatNumber color="teal.500">{stats.convertedPlaces}</StatNumber>
                  <StatHelpText>
                    {stats.contactedPlaces > 0 ? Math.round((stats.convertedPlaces / stats.contactedPlaces) * 100) : 0}% de conversão
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Precisam Retorno</StatLabel>
                  <StatNumber color="orange.500">{stats.pendingReturns}</StatNumber>
                  <StatHelpText>
                    <Badge colorScheme={stats.pendingReturns > 0 ? "orange" : "green"}>
                      {stats.pendingReturns > 0 ? "Atenção" : "Em dia"}
                    </Badge>
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Taxa Geral</StatLabel>
                  <StatNumber color="cyan.500">
                    {stats.totalPlaces > 0 ? Math.round((stats.convertedPlaces / stats.totalPlaces) * 100) : 0}%
                  </StatNumber>
                  <StatHelpText>
                    conversão total
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        {/* Notificações de Retorno */}
        {placesNeedingReturn.length > 0 && (
          <ReturnNotifications
            places={placesNeedingReturn}
            onUpdate={loadData}
          />
        )}

        {/* Lista de Listas de Prospecção */}
        <Box>
          <Heading size="md" mb={4}>
            Suas Listas de Prospecção
          </Heading>

          {prospectLists.length === 0 ? (
            <Card>
              <CardBody>
                <VStack spacing={4} py={8}>
                  <Text color="gray.500" fontSize="lg">
                    Nenhuma lista de prospecção encontrada
                  </Text>
                  <Text color="gray.400" textAlign="center">
                    Crie sua primeira lista para começar a prospectar novos clientes
                  </Text>
                  <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onOpen}>
                    Criar Primeira Lista
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={6}>
              {prospectLists.map((list) => (
                <ProspectListCard
                  key={list.id}
                  list={list}
                  onDelete={handleDeleteList}
                  onUpdate={loadData}
                />
              ))}
            </Grid>
          )}
        </Box>

        {/* Modal para Nova Lista */}
        <NovaListaModal
          isOpen={isOpen}
          onClose={onClose}
          onListCreated={handleNewListCreated}
        />
      </VStack>
    </Container>
  );
};

export default Prospeccao;